import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const ModifySecondaryEmail = ({ userId }) => {
  const [state, setState] = useState({ isLoaded: false, error: null, success: false });
  const [text, setText] = useState("");

  const { userModule, setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  useEffect(() => {
    if (userModule?.userData?.["Secondary Email"]) {
      setText(userModule.userData["Secondary Email"]);
    }
    setState((prev) => ({ ...prev, isLoaded: true }));
  }, []);

  const modifySecondaryEmail = async () => {
    setState((prev) => ({ ...prev, isLoaded: false }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/email/secondary/set`,
        {
          method: "POST",
          signal: AbortSignal.timeout(360000),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: authState.accessToken.accessToken,
          },
          body: JSON.stringify({ userId, secondaryEmail: text, callerId: authState.accessToken.claims.sub }),
        }
      );

      const data = await response.json();

      if (!response.ok) throw new Error(data?.error?.message || "Failed to update secondary email.");

      setUserModule((prev) => ({
        ...prev,
        userData: { ...prev.userData, "Secondary Email": text },
      }));

      setState({ isLoaded: true, error: null, success: true });
    } catch (err) {
      setState({ isLoaded: true, error: err, success: false });
    }
  };

  if (!state.isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.success) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Secondary Email Updated</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">Secondary email updated to {text}</Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Error Banner */}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Update failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      )}

      {/* ── Section: Secondary Email ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Secondary Email
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <Box sx={{ px: 3, pt: 3, pb: 3 }}>
        <TextField
          required
          fullWidth
          size="small"
          label="Secondary Email"
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={fieldSx}
        />
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={modifySecondaryEmail}
          disabled={!text.trim()}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          Update Secondary Email
        </Button>
      </Box>

    </TableContainer>
  );
};
